import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Scale,
  FileText,
  Lock,
  Archive
} from 'lucide-react';

interface ComplianceCheckResult {
  id: string;
  content_id: string;
  content_title: string;
  check_type: 'gdpr' | 'tos' | 'copyright' | 'data_retention';
  status: 'compliant' | 'non_compliant' | 'warning' | 'pending';
  score: number;
  issues: string[];
  recommendations: string[];
  last_checked: string;
  auto_action_taken?: string;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'compliant': return <CheckCircle className="h-4 w-4 text-green-600" />;
    case 'warning': return <AlertTriangle className="h-4 w-4 text-black" />;
    case 'non_compliant': return <AlertTriangle className="h-4 w-4 text-red-600" />;
    case 'pending': return <Clock className="h-4 w-4 text-black" />;
    default: return <Shield className="h-4 w-4" />;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'compliant': return 'bg-white text-green-800';
    case 'warning': return 'bg-white text-black';
    case 'non_compliant': return 'bg-red-100 text-red-800';
    case 'pending': return 'bg-white text-black';
    default: return 'bg-white text-black';
  }
};

const getCheckTypeIcon = (type: string) => {
  switch (type) {
    case 'gdpr': return <Shield className="h-4 w-4" />;
    case 'tos': return <FileText className="h-4 w-4" />;
    case 'copyright': return <Scale className="h-4 w-4" />;
    case 'data_retention': return <Archive className="h-4 w-4" />;
    default: return <Shield className="h-4 w-4" />;
  }
};

const AutomatedComplianceChecker = forwardRef<any, {}>((props, ref) => {
  const [results, setResults] = useState<ComplianceCheckResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    fetchComplianceResults();
  }, []);

  const fetchComplianceResults = async () => {
    try {
      let { data, error } = await supabase
        .from('compliance_violations')
        .select(`
          id,
          content_id,
          content_type,
          violation_type,
          severity,
          status,
          created_at,
          resolved_at,
          violation_data
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Map backend data to ComplianceCheckResult[]
      const mapped: ComplianceCheckResult[] = (data ?? []).map((row: any) => ({
        id: row.id,
        content_id: row.content_id,
        content_title: row.content_type || "Content",
        check_type: (row.violation_type === "copyright" ? "copyright" : row.violation_type === "gdpr" ? "gdpr" : "others") as ComplianceCheckResult["check_type"],
        status: row.status === "resolved" ? "compliant" : (row.severity === "critical" || row.status === "open" ? "non_compliant" : "warning"),
        score: row.violation_data?.score || 0,
        issues: row.violation_data?.issues || [],
        recommendations: row.violation_data?.recommendations || [],
        last_checked: row.resolved_at || row.created_at,
        auto_action_taken: row.violation_data?.auto_action_taken,
      }));
      setResults(mapped);
    } catch (error) {
      console.error('Error fetching compliance results:', error);
      toast({
        title: "Error",
        description: "Failed to fetch compliance results",
        variant: "destructive",
      });
    }
  };

  const runAutomatedCheck = async () => {
    setIsRunning(true);
    setProgress(0);

    try {
      // Simulate automated compliance checking process
      for (let i = 0; i <= 100; i += 10) {
        setProgress(i);
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // In real implementation, this would trigger backend compliance scanners
      await new Promise(resolve => setTimeout(resolve, 1000));

      toast({
        title: "Compliance Check Complete",
        description: "Automated compliance scan completed successfully",
      });

      fetchComplianceResults();
    } catch (error) {
      toast({
        title: "Check Failed",
        description: "Automated compliance check encountered errors",
        variant: "destructive",
      });
    } finally {
      setIsRunning(false);
      setProgress(0);
    }
  };

  const takeAction = async (resultId: string, action: 'lock' | 'archive' | 'flag') => {
    try {
      // Simulate taking automated action
      const actionLabels = {
        lock: 'Content locked for compliance review',
        archive: 'Content archived due to compliance issues',
        flag: 'Content flagged for manual review'
      };

      toast({
        title: "Action Taken",
        description: actionLabels[action],
      });

      // Update results to reflect action taken
      setResults(prev => prev.map(result => 
        result.id === resultId 
          ? { ...result, auto_action_taken: actionLabels[action] }
          : result
      ));
    } catch (error) {
      toast({
        title: "Action Failed",
        description: "Failed to execute automated action",
        variant: "destructive",
      });
    }
  };

  // Expose runAutomatedCheck method via ref
  useImperativeHandle(ref, () => ({
    runAutomatedCheck,
  }));

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Automated Compliance Checker
            </CardTitle>
            <Button 
              onClick={runAutomatedCheck} 
              disabled={isRunning}
              className="flex items-center gap-2"
            >
              <Shield className="h-4 w-4" />
              {isRunning ? 'Scanning...' : 'Run Full Scan'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isRunning && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Scanning content...</span>
                <span className="text-sm text-black">{progress}%</span>
              </div>
              <Progress value={progress} className="w-full" />
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-4 mb-6">
            <div className="bg-white p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">Compliant</span>
              </div>
              <div className="text-2xl font-bold text-green-600">
                {results.filter(r => r.status === 'compliant').length}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-black" />
                <span className="font-medium text-black">Warnings</span>
              </div>
              <div className="text-2xl font-bold text-black">
                {results.filter(r => r.status === 'warning').length}
              </div>
            </div>

            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="font-medium text-red-800">Non-Compliant</span>
              </div>
              <div className="text-2xl font-bold text-red-600">
                {results.filter(r => r.status === 'non_compliant').length}
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-black" />
                <span className="font-medium text-black">Pending</span>
              </div>
              <div className="text-2xl font-bold text-black">
                {results.filter(r => r.status === 'pending').length}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {results.map((result) => (
              <Card key={result.id} className="border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {getCheckTypeIcon(result.check_type)}
                      <div>
                        <h4 className="font-medium">{result.content_title}</h4>
                        <p className="text-sm text-black">
                          {result.check_type.toUpperCase()} Check
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(result.status)}>
                        {getStatusIcon(result.status)}
                        {result.status.replace('_', ' ')}
                      </Badge>
                      <span className="text-sm font-medium">{result.score}%</span>
                    </div>
                  </div>

                  {result.issues.length > 0 && (
                    <Alert className="mb-3">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Issues Found:</strong>
                        <ul className="list-disc list-inside mt-1">
                          {result.issues.map((issue, index) => (
                            <li key={index} className="text-sm">{issue}</li>
                          ))}
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {result.recommendations.length > 0 && (
                    <div className="mb-3">
                      <strong className="text-sm">Recommendations:</strong>
                      <ul className="list-disc list-inside mt-1 text-sm text-black">
                        {result.recommendations.map((rec, index) => (
                          <li key={index}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.auto_action_taken && (
                    <div className="mb-3 p-2 bg-white rounded text-sm">
                      <strong>Auto-Action:</strong> {result.auto_action_taken}
                    </div>
                  )}

                  {result.status === 'non_compliant' && !result.auto_action_taken && (
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => takeAction(result.id, 'flag')}
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        Flag for Review
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => takeAction(result.id, 'lock')}
                      >
                        <Lock className="h-3 w-3 mr-1" />
                        Lock Content
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => takeAction(result.id, 'archive')}
                      >
                        <Archive className="h-3 w-3 mr-1" />
                        Archive
                      </Button>
                    </div>
                  )}

                  <div className="text-xs text-black mt-2">
                    Last checked: {new Date(result.last_checked).toLocaleString()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
});

AutomatedComplianceChecker.displayName = 'AutomatedComplianceChecker';
export default AutomatedComplianceChecker;
